import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { ImageUploader } from "../components/VideoUploader";
import { Users, Plus, Loader2 } from "lucide-react";
import { fileUrl } from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { useInfiniteList } from "../lib/useInfiniteList";

const PAGE = 4;

export default function Groups() {
  const [q, setQ] = useState("");
  const [qCommitted, setQCommitted] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", is_private: false, cover_image: null });

  const { items: groups, loading, loadingMore, hasMore, sentinelRef } = useInfiniteList(
    (skip, limit) => {
      const search = qCommitted ? `&q=${encodeURIComponent(qCommitted)}` : "";
      return api.get(`/groups?limit=${limit}&skip=${skip}${search}`).then((r) => r.data);
    },
    [qCommitted, refreshKey],
    PAGE
  );

  const create = async () => {
    if (!form.name) { toast.error("Group name required"); return; }
    try {
      await api.post("/groups", form);
      toast.success("Group created");
      setOpen(false);
      setForm({ name: "", description: "", is_private: false, cover_image: null });
      setRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error("Failed to create");
    }
  };

  const join = async (id) => {
    try { await api.post(`/groups/${id}/join`); toast.success("Joined"); setRefreshKey((k) => k + 1); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-6 lg:py-10">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8 border-b border-border pb-6">
        <div>
          <div className="text-xs font-semibold text-muted-foreground mb-1 tracking-wider">COMMUNITIES</div>
          <h1 className="font-serif text-4xl">Groups</h1>
        </div>
        <div className="flex gap-2">
          <Input
            className="rounded-full bg-card border-border h-11 w-64"
            placeholder="Search groups…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && setQCommitted(q)}
            onBlur={() => qCommitted !== q && setQCommitted(q)}
            data-testid="groups-search-input"
          />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full h-11 bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="create-group-btn">
                <Plus className="w-4 h-4 mr-2" /> Create group
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl bg-card border-border">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">New group</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-semibold">Name</Label>
                  <Input className="rounded-xl mt-1 bg-background border-border" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="group-name-input" />
                </div>
                <div>
                  <Label className="text-sm font-semibold">Description</Label>
                  <Textarea className="rounded-xl mt-1 bg-background border-border" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="group-desc-input" />
                </div>
                <div>
                  <Label className="text-sm font-semibold mb-2 block">Banner image</Label>
                  <ImageUploader onUploaded={(path) => setForm({ ...form, cover_image: path })} testId="group-banner" label="Upload banner" />
                </div>
                <div className="flex items-center justify-between border border-border rounded-xl p-3">
                  <div className="text-sm font-semibold">Private group</div>
                  <Switch checked={form.is_private} onCheckedChange={(v) => setForm({ ...form, is_private: v })} data-testid="group-private-switch" />
                </div>
                <Button onClick={create} className="w-full rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="group-submit">
                  Create
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading ? (
        <div className="text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : groups.length === 0 ? (
        <div className="border border-dashed border-border rounded-2xl p-16 text-center text-muted-foreground">
          {qCommitted ? `No groups match "${qCommitted}"` : "No groups yet — be the first to start a community"}
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 fade-in stagger" data-testid="groups-grid">
            {groups.map((g) => (
              <div key={g.id} className="border border-border bg-card rounded-2xl overflow-hidden card-shadow" data-testid={`group-card-${g.id}`}>
                <div className="aspect-[2/1] bg-gradient-to-br from-[hsl(var(--primary-soft))] to-[hsl(var(--secondary))] flex items-center justify-center border-b border-border relative">
                  {g.cover_image ? (
                    <img src={fileUrl(g.cover_image)} alt={g.name} className="w-full h-full object-cover" />
                  ) : (
                    <Users className="w-10 h-10 text-[hsl(var(--primary))]" />
                  )}
                </div>
                <div className="p-5">
                  <div className="text-xs text-muted-foreground mb-2">
                    {g.is_private ? "Private" : "Public"} · {g.members_count} members
                  </div>
                  <Link to={`/groups/${g.id}`} className="font-serif text-2xl mb-3 block hover:text-[hsl(var(--primary))] transition-colors">{g.name}</Link>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-4 h-10">
                    {g.description || "No description provided."}
                  </p>
                  <div className="flex gap-2">
                    <Link to={`/groups/${g.id}`} className="flex-1">
                      <Button variant="outline" className="w-full rounded-full" data-testid={`view-group-${g.id}`}>View</Button>
                    </Link>
                    {!g.is_private && (
                      <Button
                        onClick={() => join(g.id)}
                        className="flex-1 rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90"
                        data-testid={`join-group-btn-${g.id}`}
                      >
                        Join
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div ref={sentinelRef} className="h-1 mt-6" data-testid="groups-sentinel" />
          {loadingMore && (
            <div className="flex items-center justify-center py-6 label-mono text-muted-foreground gap-2" data-testid="groups-loading-more">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading more…
            </div>
          )}
          {!hasMore && groups.length > PAGE && (
            <div className="text-center label-mono text-muted-foreground py-6" data-testid="groups-end">
              You&apos;ve reached the end · {groups.length} groups
            </div>
          )}
        </>
      )}
    </div>
  );
}
